import { openDB } from 'idb';

class InspectionStorage {
  constructor() {
    this.DB_NAME = 'RapteckMobile';
    this.STORE_NAME = 'inspections';
    this.DB_VERSION = 2;
    this.dbPromise = null;
    this.suspendAutoSync = false;
  }

  async getDB() {
    if (!this.dbPromise) {
      this.dbPromise = openDB(this.DB_NAME, this.DB_VERSION, {
        upgrade(db, oldVersion, newVersion, transaction) {
          console.log(`🔄 Upgrade IndexedDB (inspectionStorage): v${oldVersion} → v${newVersion}`);
          
          if (!db.objectStoreNames.contains('inspections')) {
            db.createObjectStore('inspections', { keyPath: 'key' });
            console.log('✅ Store "inspections" créé');
          }

          if (!db.objectStoreNames.contains('settings')) {
            db.createObjectStore('settings');
            console.log('✅ Store "settings" créé');
          }

          if (!db.objectStoreNames.contains('referentiel')) {
            db.createObjectStore('referentiel');
            console.log('✅ Store "referentiel" créé');
          }
        },
      });
    }
    return this.dbPromise;
  }

  async set(key, value) {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      
      const data = {
        key: key,
        value: JSON.parse(JSON.stringify(value)),
        timestamp: Date.now()
      };
      
      await store.put(data);
      await tx.done;
      console.log(`✅ Donnée enregistrée: ${key}`);
    } catch (error) {
      console.error('❌ Erreur set:', error);
      throw error;
    }
  }

  async get(key) {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const data = await store.get(key);
      
      if (data) {
        console.log(`✅ Donnée récupérée: ${key}`);
        return data.value;
      }
      return null;
    } catch (error) {
      console.error('❌ Erreur get:', error);
      return null;
    }
  }

  async delete(key) {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      await store.delete(key);
      await tx.done;
      console.log(`✅ Donnée supprimée: ${key}`);
    } catch (error) {
      console.error('❌ Erreur delete:', error);
      throw error;
    }
  }

  async saveStep(missionId, stepKey, stepData) {
    const key = `mission_${missionId}_${stepKey}`;
    await this.set(key, stepData);
    await this.set(`mission_${missionId}_last_local_update`, new Date().toISOString());

    if (this.suspendAutoSync) {
      return;
    }

    try {
      const { default: syncManager } = await import('./syncManager');
      syncManager.syncStep(missionId, stepKey, stepData);
    } catch (error) {
      console.warn('⚠️ Auto-sync indisponible:', error?.message || error);
    }
  }

  async getStep(missionId, stepKey) {
    const key = `mission_${missionId}_${stepKey}`;
    return await this.get(key);
  }

  async getInspection(missionId) {
    return await this.getAllSteps(missionId);
  }

  async getAllSteps(missionId) {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const allData = await store.getAll();
      
      const prefix = `mission_${missionId}_`;
      const missionData = {};
      
      allData.forEach(item => {
        if (item.key.startsWith(prefix)) {
          const stepKey = item.key.replace(prefix, '');
          missionData[stepKey] = item.value;
        }
      });
      
      return missionData;
    } catch (error) {
      console.error('❌ Erreur getAllSteps:', error);
      return {};
    }
  }

  async getProgress(missionId) {
    try {
      const allSteps = await this.getAllSteps(missionId);
      const totalSteps = 14;
      const completedSteps = Object.keys(allSteps).length;
      
      return {
        completed: completedSteps,
        total: totalSteps,
        percentage: Math.round((completedSteps / totalSteps) * 100)
      };
    } catch (error) {
      console.error('❌ Erreur getProgress:', error);
      return {
        completed: 0,
        total: 14,
        percentage: 0
      };
    }
  }

  async deleteMission(missionId) {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const allKeys = await store.getAllKeys();
      
      const prefix = `mission_${missionId}_`;
      const keysToDelete = allKeys.filter(key => key.startsWith(prefix));
      
      for (const key of keysToDelete) {
        await store.delete(key);
      }
      
      await tx.done;
      console.log(`✅ Mission ${missionId} supprimée (${keysToDelete.length} étapes)`);
    } catch (error) {
      console.error('❌ Erreur deleteMission:', error);
      throw error;
    }
  }

  async hasMissionData(missionId) {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const allKeys = await store.getAllKeys();
      
      const prefix = `mission_${missionId}_`;
      return allKeys.some(key => key.startsWith(prefix));
    } catch (error) {
      console.error('❌ Erreur hasMissionData:', error);
      return false;
    }
  }

  async getAllMissionsWithData() {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const allKeys = await store.getAllKeys();
      
      const missionIds = new Set();
      allKeys.forEach(key => {
        const match = key.match(/^mission_(\d+)_/);
        if (match) {
          missionIds.add(match[1]);
        }
      });
      
      return Array.from(missionIds);
    } catch (error) {
      console.error('❌ Erreur getAllMissionsWithData:', error);
      return [];
    }
  }

  async getStorageSize() {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const allData = await store.getAll();
      
      const size = JSON.stringify(allData).length;
      return {
        bytes: size,
        kb: (size / 1024).toFixed(2),
        mb: (size / 1024 / 1024).toFixed(2)
      };
    } catch (error) {
      console.error('❌ Erreur getStorageSize:', error);
      return { bytes: 0, kb: 0, mb: 0 };
    }
  }

  async clearAll() {
    try {
      const db = await this.getDB();
      const tx = db.transaction([this.STORE_NAME], 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      await store.clear();
      await tx.done;
      console.log('✅ Stockage vidé complètement');
    } catch (error) {
      console.error('❌ Erreur clearAll:', error);
      throw error;
    }
  }

  async exportMissionData(missionId) {
    try {
      const data = await this.getAllSteps(missionId);
      return JSON.stringify(data, null, 2);
    } catch (error) {
      console.error('❌ Erreur exportMissionData:', error);
      return null;
    }
  }

  async importMissionData(missionId, jsonData) {
    try {
      const data = JSON.parse(jsonData);
      
      for (const [stepKey, stepData] of Object.entries(data)) {
        await this.saveStep(missionId, stepKey, stepData);
      }
      
      console.log(`✅ Données importées pour mission ${missionId}`);
      return true;
    } catch (error) {
      console.error('❌ Erreur importMissionData:', error);
      return false;
    }
  }

  async markAsSynced(missionId) {
    try {
      const key = `mission_${missionId}_sync_status`;
      const syncedAt = new Date().toISOString();
      await this.set(key, {
        synced: true,
        synced_at: syncedAt
      });
      await this.set(`mission_${missionId}_last_sync`, syncedAt);
      console.log(`✅ Mission ${missionId} marquée comme synchronisée`);
    } catch (error) {
      console.error('❌ Erreur markAsSynced:', error);
    }
  }

  async getLastLocalUpdate(missionId) {
    return await this.get(`mission_${missionId}_last_local_update`);
  }

  async getLastSync(missionId) {
    return await this.get(`mission_${missionId}_last_sync`);
  }

  async isDirty(missionId) {
    try {
      const [localUpdate, lastSync] = await Promise.all([
        this.getLastLocalUpdate(missionId),
        this.getLastSync(missionId),
      ]);
      if (!localUpdate) return false;
      if (!lastSync) return true;
      return new Date(localUpdate).getTime() > new Date(lastSync).getTime();
    } catch (error) {
      console.error('❌ Erreur isDirty:', error);
      return true;
    }
  }

  async isSynced(missionId) {
    try {
      const key = `mission_${missionId}_sync_status`;
      const status = await this.get(key);
      return status?.synced === true;
    } catch (error) {
      console.error('❌ Erreur isSynced:', error);
      return false;
    }
  }

  /**
   * 🔥 Synchronise les données d'une mission depuis le serveur Base44
   * Mappe les étapes Base44 → PWA et stocke dans IndexedDB
   */
  async syncFromServer(missionId, serverData) {
    try {
      console.log(`🔄 syncFromServer mission ${missionId}...`);
      this.suspendAutoSync = true;

      // 1. Sauvegarder les métadonnées de la mission
      const missionMeta = {
        id: serverData.id,
        numero_rapport: serverData.numero_rapport,
        client_id: serverData.client_id,
        client_nom: serverData.client_nom,
        etablissement_id: serverData.etablissement_id,
        etablissement: serverData.etablissement,
        activite_etablissement: serverData.activite_etablissement,
        site_adresse: serverData.site_adresse,
        date_verification: serverData.date_verification,
        activite: serverData.activite,
        type_verification: serverData.type_verification,
        inspecteur_id: serverData.inspecteur_id,
        verificateur: serverData.verificateur,
        client: serverData.client,
        etablissement_data: serverData.etablissement_data,
        inspecteur: serverData.inspecteur || null,
      };
      await this.saveStep(missionId, 'meta', missionMeta);
      console.log(`✅ Métadonnées sauvegardées`);
      
      // 2. Mapper et sauvegarder chaque étape (mêmes clés que Base44)
      const stepsToSave = {
        etape_1_infos_generales: serverData.etape_1_infos_generales,
        etape_2_normes: serverData.etape_2_normes,
        etape_3_conditions: serverData.etape_3_conditions,
        etape_4_documents: serverData.etape_4_documents,
        etape_5_prises_terre: serverData.etape_5_prises_terre,
        etape_6_classification: serverData.etape_6_classification,
        etape_7_securite: serverData.etape_7_securite,
        etape_8_securite: serverData.etape_7_securite,
        etape_8_sources_energie: serverData.etape_8_sources_energie,
        etape_9_armoires: serverData.etape_9_armoires,
        etape_10_localisations: serverData.etape_10_localisations,
        etape_11_observations: serverData.etape_11_observations,
        etape_12_conformite: serverData.etape_12_conformite,
        etape_13_finalisation: serverData.etape_13_finalisation,
        etape_18_caracteristiques_erp: serverData.etape_18_caracteristiques_erp,
        etape_19_materiels_mesure: serverData.etape_19_materiels_mesure,
        etape_20_avis_conformite_rvrat: serverData.etape_20_avis_conformite_rvrat,
        rapport_q19_data: serverData.rapport_q19_data
      };
      
      for (const [stepKey, stepData] of Object.entries(stepsToSave)) {
        if (stepData && typeof stepData === 'object' && Object.keys(stepData).length > 0) {
          await this.saveStep(missionId, stepKey, stepData);
          console.log(`✅ ${stepKey} sauvegardé`);
        }
      }
      
      // 3. Marquer comme synchronisé
      await this.markAsSynced(missionId);
      if (serverData?.updated_at) {
        await this.set(`mission_${missionId}_last_sync`, serverData.updated_at);
      }
      
      console.log(`✅ Mission ${missionId} synchronisée depuis serveur`);
      return true;
    } catch (error) {
      console.error('❌ Erreur syncFromServer:', error);
      return false;
    } finally {
      this.suspendAutoSync = false;
    }
  }

  /**
   * 🔥 Convertit etape_6_classification - SANS transformation de valeurs
   */
  convertClassificationFromServer(serverData) {
    if (!serverData) return { locaux: [], classement_locaux: 'propose', zones_explosion: 'sans_objet' };
    
    // Retourner les données telles quelles - mêmes clés que Base44
    return {
      locaux: serverData.locaux || [],
      classement_locaux: serverData.classement_locaux || 'propose',
      zones_explosion: serverData.zones_explosion || 'sans_objet'
    };
  }

  /**
   * 🔥 Convertit etape_7_securite - SANS transformation de valeurs
   */
  convertSecuriteFromServer(serverData) {
    if (!serverData) return { installations: [], sans_objet: false };
    
    // Retourner les données telles quelles
    return {
      installations: serverData.installations || [],
      sans_objet: serverData.sans_objet || false
    };
  }
}

export default new InspectionStorage();